import { ccPostCommitment } from '../api-client';

type Direction = 'out' | 'in';
type Recurrence = 'one_off' | 'monthly' | 'yearly' | 'weekly';

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function openCommitmentSheet(onDone: () => void): void {
  let direction: Direction = 'out';
  let recurrence: Recurrence = 'one_off';
  let isCard = false;

  const overlay = document.createElement('div');
  document.body.appendChild(overlay);

  const close = () => { overlay.remove(); };

  function accountLabel(): string {
    return direction === 'out' ? 'Pay from' : 'Receive into';
  }

  function render(): void {
    const isOneOff = recurrence === 'one_off';

    overlay.innerHTML = `
      <div class="sheet-backdrop" id="cs-backdrop">
        <div class="sheet commitment-sheet" role="dialog" aria-modal="true">
          <header class="sheet-header">
            <h3>+ Commitment</h3>
            <button class="ghost sheet-close" id="cs-close" type="button" aria-label="Close">✕</button>
          </header>
          <div class="sheet-body" id="cs-body">
            <div class="cc-dir-toggle">
              <button class="cc-dir-btn${direction === 'out' ? ' on' : ''}" data-dir="out" type="button">Money out</button>
              <button class="cc-dir-btn${direction === 'in' ? ' on' : ''}" data-dir="in" type="button">Money in</button>
            </div>

            <label class="sheet-field">
              <span class="sheet-label">Name</span>
              <input data-field="name" type="text" placeholder="e.g. Rent" autocomplete="off" />
            </label>

            <label class="sheet-field cc-card-toggle-row">
              <span class="sheet-label">Card payment</span>
              <input data-card-toggle type="checkbox"${isCard ? ' checked' : ''} />
            </label>
            ${isCard ? `
            <label class="sheet-field">
              <span class="sheet-label">Minimum payment (optional)</span>
              <input data-field="min-payment" type="text" inputmode="decimal" placeholder="0.00" autocomplete="off" />
            </label>` : ''}

            <label class="sheet-field">
              <span class="sheet-label">Amount</span>
              <input data-field="amount" type="text" inputmode="decimal" placeholder="0.00" autocomplete="off" />
            </label>

            <label class="sheet-field">
              <span class="sheet-label">Currency</span>
              <input data-field="currency" type="text" value="AED" maxlength="3" autocomplete="off" />
            </label>

            <label class="sheet-field">
              <span class="sheet-label" id="cs-account-label">${escapeHtml(accountLabel())}</span>
              <input data-field="account" type="text" placeholder="Optional" autocomplete="off" />
            </label>

            <label class="sheet-field">
              <span class="sheet-label">Recurrence</span>
              <select data-field="recurrence">
                <option value="one_off"${recurrence === 'one_off' ? ' selected' : ''}>One-off</option>
                <option value="monthly"${recurrence === 'monthly' ? ' selected' : ''}>Monthly</option>
                <option value="yearly"${recurrence === 'yearly' ? ' selected' : ''}>Yearly</option>
                <option value="weekly"${recurrence === 'weekly' ? ' selected' : ''}>Weekly</option>
              </select>
            </label>

            ${isOneOff ? `
              <label class="sheet-field">
                <span class="sheet-label">Due date</span>
                <input data-field="anchor-date" type="date" value="${escapeHtml(todayIso())}" />
              </label>
            ` : `
              <label class="sheet-field">
                <span class="sheet-label">Anchor day</span>
                <input data-field="anchor-date" type="date" value="${escapeHtml(todayIso())}" />
              </label>
            `}

            ${direction === 'in' ? `
              <label class="sheet-field cc-confirmed-row">
                <span class="sheet-label">Confirmed</span>
                <input data-field="confirmed" type="checkbox" />
              </label>
            ` : ''}

            <div class="cs-error" id="cs-error" hidden></div>

            <div class="sheet-actions">
              <button class="ghost" id="cs-cancel" type="button">Cancel</button>
              <button class="primary" data-action="save-commitment" id="cs-save" type="button">Save</button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Backdrop dismiss
    overlay.querySelector<HTMLElement>('#cs-backdrop')!.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) close();
    });
    overlay.querySelector<HTMLButtonElement>('#cs-close')!.addEventListener('click', close);
    overlay.querySelector<HTMLButtonElement>('#cs-cancel')!.addEventListener('click', close);

    // Direction toggle
    overlay.querySelectorAll<HTMLButtonElement>('[data-dir]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const d = btn.getAttribute('data-dir') as Direction;
        if (d === 'out' || d === 'in') {
          direction = d;
          render();
        }
      });
    });

    // Recurrence change
    const recurrenceEl = overlay.querySelector('[data-field="recurrence"]') as HTMLSelectElement | null;
    recurrenceEl?.addEventListener('change', () => {
      recurrence = recurrenceEl.value as Recurrence;
      render();
    });

    // Card-payment toggle
    const cardToggle = overlay.querySelector('[data-card-toggle]') as HTMLInputElement | null;
    cardToggle?.addEventListener('change', () => { isCard = cardToggle.checked; render(); });

    // Save
    const saveBtn = overlay.querySelector<HTMLButtonElement>('[data-action="save-commitment"]')!;
    const errEl = overlay.querySelector<HTMLElement>('#cs-error')!;

    saveBtn.addEventListener('click', async () => {
      errEl.hidden = true;

      const name = (overlay.querySelector<HTMLInputElement>('[data-field="name"]')!.value.trim());
      const amountRaw = overlay.querySelector<HTMLInputElement>('[data-field="amount"]')!.value.trim();
      const currency = (overlay.querySelector<HTMLInputElement>('[data-field="currency"]')!.value.trim().toUpperCase()) || 'AED';
      const anchorDate = overlay.querySelector<HTMLInputElement>('[data-field="anchor-date"]')!.value || todayIso();
      const confirmedEl = overlay.querySelector<HTMLInputElement>('[data-field="confirmed"]');
      const confirmed = confirmedEl?.checked ?? false;

      if (!name) {
        errEl.textContent = 'Enter a name.';
        errEl.hidden = false;
        return;
      }

      const amount = parseFloat(amountRaw);
      if (isNaN(amount) || amount <= 0) {
        errEl.textContent = 'Enter a valid amount.';
        errEl.hidden = false;
        return;
      }

      saveBtn.disabled = true;
      try {
        const body: Parameters<typeof ccPostCommitment>[0] = {
          name,
          amount,
          currency,
          direction,
          recurrence,
          anchorDate,
        };
        if (direction === 'in') body.confirmed = confirmed;
        if (isCard) {
          body.commitmentType = 'card';
          const minRaw = overlay.querySelector<HTMLInputElement>('[data-field="min-payment"]')?.value.trim() ?? '';
          if (minRaw) {
            const min = parseFloat(minRaw);
            if (!isNaN(min) && min >= 0) body.minPayment = min;
          }
        }
        await ccPostCommitment(body);
        close();
        onDone();
      } catch (e) {
        errEl.textContent = e instanceof Error ? e.message : String(e);
        errEl.hidden = false;
        saveBtn.disabled = false;
      }
    });
  }

  render();
}
